import { HostListener, Injectable, OnDestroy } from '@angular/core';
import { AssetManager } from './asset-manager';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { InspectionPointInstanceManager } from '../digital-twin/graphics/inspection-point-instance-manager.service';
import { GaugingElement } from '../platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { INSERTIONCONSTANTS } from 'projects/phdsoft/common/src/lib/shared/constants/insertions-constants';
import { StructuralElement } from '@app/platform/busines-data/SectionElements/StructureElements/StructuralElement';
import { PhantomSurfaceDesign } from '../digital-twin/graphics/phantom-surface-design';

@Injectable({ providedIn: 'root' })
export class AssetManagerDispose implements OnDestroy {

  constructor(
    private assetManager: AssetManager,
    private instanceManager: InspectionPointInstanceManager,
  ) { }

  @HostListener('unloaded')
  ngOnDestroy(): void {
    console.log('💣 Destroy AssetManagerDispose');
  }

  public disposeInspectionElement(element: ElementResultInfo, collectionId: string): void {
    const collection = this.assetManager.asset.InspectionCampaigns.find(x => x.CollectionInfoModel.CollectionId === collectionId ||
      x.CollectionInfoModel.TempCollectionId === collectionId);

    if (collection) {
      // Resolve the live GaugingElement before removing it from the collection
      // so we can call the InstancedMesh manager for POINT inspections (SEL-02).
      const gaugingElement = collection.Elements.find(x => x.Guid === element.id) as GaugingElement | undefined;

      // InstancedMesh lifecycle management for POINT inspections only.
      // AREA and ELEMENT inspections use their own real meshes and are out of scope.
      if (gaugingElement && gaugingElement.inspectionMethodId === INSERTIONCONSTANTS.POINT) {
        if (!gaugingElement.saved) {
          // Cancel path: element was never saved to backend.
          // Swap-and-pop compaction — removes the slot entirely, no dead slots.
          // Locked decision: "On cancel: full cleanup — remove the instance slot entirely."
          this.instanceManager.removeInstance(gaugingElement);
        } else {
          // Delete path: element was saved but is now being removed from the scene.
          // Tombstone with zero-scale matrix so the point disappears visually without
          // rebuilding the InstancedMesh. The slot is preserved for undo/history.
          // Locked decision: "On delete: tombstone — setInstanceVisible(false)."
          this.instanceManager.setInstanceVisible(gaugingElement, false);
        }
      }

      collection.deleteElementByIdOnly(collection.regionId, element.id);

      const tempInsertedPointsArrayIndex: number = this.assetManager.tempInsertedPointsArray.findIndex(x => x.Guid == element.id);
      if (tempInsertedPointsArrayIndex !== -1) {
        this.assetManager.tempInsertedPointsArray.splice(tempInsertedPointsArrayIndex, 1);
      }
    }
  }

  /**
   * Soft detach: take the element out of the scene and out of every element list
   * WITHOUT disposing geometry. The live GaugingElement is kept for reattachment.
   *
   * POINT inspections are InstancedMesh slots behind a PhantomSurfaceDesign: there
   * is no THREE parent to remove from, and element.section points at the
   * STRUCTURE section (loader invariant) while the element is listed in the
   * inspection campaign's own section. Both are handled explicitly here.
   */
  public detachInspectionElement(element: GaugingElement, collectionId: string): void {
    const campaign = this.assetManager.asset.InspectionCampaigns.find(
      x => x.CollectionInfoModel.CollectionId === collectionId ||
           x.CollectionInfoModel.TempCollectionId === collectionId
    );
    if (!campaign) return;

    if (element.SurfaceDesign instanceof PhantomSurfaceDesign) {
      // Instanced point: tombstone the slot (zero-scale) so it disappears now and
      // can be shown again by reattachInspectionElement on rollback.
      this.instanceManager.setInstanceVisible(element, false);
    } else if (element.SurfaceDesign?.parent) {
      // Real mesh (AREA / ELEMENT): detach from THREE parent, keep geometry.
      element.SurfaceDesign.parent.remove(element.SurfaceDesign);
    }

    // Remove from the campaign section the element is actually listed in.
    const campaignSection = this.findCampaignSection(campaign, element);
    this.removeFromSurfaceElements(campaignSection, element);
    // Legacy path: real-mesh inspections may be listed on element.section itself.
    if (element.section && element.section !== campaignSection) {
      this.removeFromSurfaceElements(element.section, element);
    }

    // Remove from targetElement.gaugingElements
    if (element.targetElement) {
      const gaugingElements = (element.targetElement as StructuralElement).gaugingElements;
      const idx = gaugingElements.findIndex(x => x.Guid === element.Guid);
      if (idx > -1) gaugingElements.splice(idx, 1);
    }

    // Remove from tempInsertedPointsArray
    const tempIdx = this.assetManager.tempInsertedPointsArray.findIndex(x => x.Guid === element.Guid);
    if (tempIdx !== -1) {
      this.assetManager.tempInsertedPointsArray.splice(tempIdx, 1);
    }
  }

  /**
   * Finalise a previously detached element. detachInspectionElement already took it out of the
   * scene graph and out of every element array, so only the GPU buffers are left to release.
   * Instanced points own no geometry (the slot stays tombstoned), so this is a no-op for them.
   */
  public disposeDetachedInspectionElement(element: GaugingElement): void {
    const surfaceDesign = element?.SurfaceDesign;
    if (!surfaceDesign || surfaceDesign instanceof PhantomSurfaceDesign) {
      return;
    }

    surfaceDesign.parent?.remove(surfaceDesign);
    surfaceDesign.geometry?.dispose();
  }

  /**
   * Reattach a previously detached element back into the scene graph and arrays.
   * Idempotent: an element still listed in its campaign section is not added twice.
   *
   * restoreAsTemporary must be false for already persisted elements: tempInsertedPointsArray only
   * tracks points that still have to be inserted, and detach never took those out of it.
   */
  public reattachInspectionElement(element: GaugingElement, collectionId: string, restoreAsTemporary: boolean = true): void {
    const campaign = this.assetManager.asset.InspectionCampaigns.find(
      x => x.CollectionInfoModel.CollectionId === collectionId ||
           x.CollectionInfoModel.TempCollectionId === collectionId
    );
    if (!campaign) return;

    const campaignSection = this.findCampaignSection(campaign, element);
    const alreadyListed = campaignSection?.surfaceElements.some(x => x.Guid === element.Guid) ?? false;
    if (!alreadyListed) {
      // Collection.addSurfaceElement overwrites element.section with the campaign's
      // internal section; instanced points must keep the structure section
      // (batch keys, phantom userData and selection fallbacks read it).
      const structureSection = element.section;
      campaign.addSurfaceElement(element);
      if (element.SurfaceDesign instanceof PhantomSurfaceDesign) {
        element.section = structureSection;
      }
    }

    if (element.SurfaceDesign instanceof PhantomSurfaceDesign) {
      this.instanceManager.setInstanceVisible(element, true);
    }

    // Re-add to parent structural element's gaugingElements
    if (element.targetElement) {
      (element.targetElement as StructuralElement).addGaugingElement(element);
    }

    // Re-add to temp tracking
    if (restoreAsTemporary) {
      this.assetManager.tempInsertedPointsArray.push(element);
    }
  }

  /** The inspection campaign's own section that lists this element (by regionId + SectionId). */
  private findCampaignSection(campaign: { regionsWorkableElements: Array<{ Guid: string; sections: Array<{ Guid: string; surfaceElements: Array<{ Guid: string }> }> }> }, element: GaugingElement) {
    return campaign.regionsWorkableElements
      .find(region => region.Guid === element.regionId)
      ?.sections.find(section => section.Guid === element.SectionId);
  }

  private removeFromSurfaceElements(section: { surfaceElements: Array<{ Guid: string }> } | undefined, element: GaugingElement): void {
    if (!section) return;
    const idx = section.surfaceElements.findIndex(x => x.Guid === element.Guid);
    if (idx > -1) section.surfaceElements.splice(idx, 1);
  }

}
